/**
 * TempPasswordDialog — one-time display of a freshly minted platform-account
 * password (Task 12, ERP Users & Access Rights epic). Fed by either the
 * "Reset password" command (runtimeApi.resetUserPassword) or a `_temp_password`
 * key riding a save result when the platform account was just minted.
 *
 * The password is never logged, never put in a URL, and never persisted —
 * this dialog (display-once) is the only place it is shown.
 */
import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { MONO_FONT } from '@/shared/typography'

export interface TempPasswordDialogProps {
  open: boolean
  password: string
  onClose: () => void
}

export const TempPasswordDialog: React.FC<TempPasswordDialogProps> = ({ open, password, onClose }) => {
  const [copied, setCopied] = useState(false)

  // A second reset/create in the same session reuses this one dialog instance
  // with a new `password` — without this, a stale "Copied" from the FIRST
  // password would still show next to the second before any click.
  useEffect(() => {
    setCopied(false)
  }, [password])

  const handleCopy = () => {
    // jsdom (and some real browsers outside a secure context) has no
    // navigator.clipboard at all — guard rather than let the click throw.
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(password)
      setCopied(true)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-label="One-time password">
      <DialogTitle>New password</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This password is shown once. Hand it to the user; they should change it after first login.
        </DialogContentText>
        <TextField
          value={password}
          fullWidth
          size="small"
          inputProps={{ 'aria-label': 'Temporary password', style: { fontFamily: MONO_FONT } }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Copy" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {copied && (
          <DialogContentText sx={{ mt: 1 }} color="success.main">
            Copied
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
